// Aceite de convite (`/invite/accept`), página pública aberta pelo link que
// o organizador manda pro apoio, pra equipe de raia ou pra equipe de apoio.
// Mesmo glossário do app: apoio → support/apoyo (nunca "rescue"), equipe de
// raia → course team/equipo de recorrido (`eventRoleCourseTeam` nas ARBs).
import { defineMessages } from '../core';

export const eventsInviteT = defineMessages('eventsInvite', {
  pt: {
    missingToken: 'Token ausente.',
    invalidTitle: 'Convite inválido',
    notFound: 'Convite não encontrado.',
    allSetTitle: 'Tudo pronto!',
    acceptedTitle: 'Convite aceito',
    /// Papel do convite aberto, no meio da frase ("atuar como apoio X").
    nounSupport: 'apoio',
    nounCourseTeam: 'equipe de raia',
    /// O mesmo papel abrindo o título ("Apoio X").
    titleSupport: 'Apoio',
    titleCourseTeam: 'Equipe de raia',
    /// `role` é `nounSupport`/`nounCourseTeam` do mesmo idioma.
    openAcceptedBody: (email: string, eventName: string, role: string, name: string) => (
      <>
        Agora baixe o app WindMaps e faça login com o email <strong>{email}</strong>. Você verá o evento{' '}
        <strong>{eventName}</strong> em "Meus eventos" e poderá atuar como {role} <strong>{name}</strong>.
      </>
    ),
    teamSentBody: 'Sua equipe foi enviada à organização para aprovação. Você será notificado quando aprovado.',
    /// Título do convite que já não aceita resposta. Antes era o valor cru
    /// do banco ("Convite expired"); a tabela é por status pra que um status
    /// novo no backend não compile sem rótulo.
    statusTitle: {
      approved: 'Convite já aprovado',
      rejected: 'Convite recusado',
      expired: 'Convite expirado',
    },
    unavailable: 'Esse convite não está mais disponível.',
    openBody: (eventName: string, name: string) => (
      <>
        {eventName}. Você é o responsável por <strong>{name}</strong>. Informe o email que você vai usar pra entrar
        no app WindMaps. É assim que o app vai te reconhecer.
      </>
    ),
    yourEmail: 'Seu email',
    emailPlaceholder: 'joao@email.com',
    emailHint:
      'Esse é o email que você vai usar no signup do app. Pode ser Gmail, Outlook ou qualquer outro, mas tem que ser o mesmo nos dois lugares.',
    teamTitle: (eventName: string) => `Convite: ${eventName}`,
    teamBody: (name: string) =>
      `Olá, ${name}. Confirme os dados da sua equipe abaixo. Depois disso, a organização aprova manualmente o cadastro.`,
    teamNameLabel: 'Nome da equipe / construtor',
    slugLabel: 'Slug',
    slugPlaceholder: 'faster-kite',
    slugHint: 'Identificador URL-safe. Usado no CSV de atletas.',
    teamColorLabel: 'Cor da equipe',
    contactNameLabel: 'Nome do responsável',
    acceptInvite: 'Aceitar convite',
  },
  en: {
    missingToken: 'Missing token.',
    invalidTitle: 'Invalid invite',
    notFound: 'Invite not found.',
    allSetTitle: 'All set!',
    acceptedTitle: 'Invite accepted',
    nounSupport: 'support',
    nounCourseTeam: 'course team',
    titleSupport: 'Support',
    titleCourseTeam: 'Course team',
    openAcceptedBody: (email, eventName, role, name) => (
      <>
        Now download the WindMaps app and sign in with the email <strong>{email}</strong>. You will see the event{' '}
        <strong>{eventName}</strong> under "My events" and will be able to act as {role} <strong>{name}</strong>.
      </>
    ),
    teamSentBody: 'Your team was sent to the organization for approval. You will be notified once it is approved.',
    statusTitle: {
      approved: 'Invite already approved',
      rejected: 'Invite declined',
      expired: 'Invite expired',
    },
    unavailable: 'This invite is no longer available.',
    openBody: (eventName, name) => (
      <>
        {eventName}. You are in charge of <strong>{name}</strong>. Enter the email you will use to sign in to the
        WindMaps app. That is how the app will recognize you.
      </>
    ),
    yourEmail: 'Your email',
    emailPlaceholder: 'john@email.com',
    emailHint:
      'This is the email you will use to sign up in the app. It can be Gmail, Outlook or any other, but it has to be the same in both places.',
    teamTitle: (eventName) => `Invite: ${eventName}`,
    teamBody: (name) =>
      `Hi, ${name}. Confirm your team details below. After that, the organization approves the registration manually.`,
    teamNameLabel: 'Team / manufacturer name',
    slugLabel: 'Slug',
    slugPlaceholder: 'faster-kite',
    slugHint: 'URL-safe identifier. Used in the athletes CSV.',
    teamColorLabel: 'Team color',
    contactNameLabel: 'Contact name',
    acceptInvite: 'Accept invite',
  },
  es: {
    missingToken: 'Falta el token.',
    invalidTitle: 'Invitación inválida',
    notFound: 'Invitación no encontrada.',
    allSetTitle: '¡Todo listo!',
    acceptedTitle: 'Invitación aceptada',
    nounSupport: 'apoyo',
    nounCourseTeam: 'equipo de recorrido',
    titleSupport: 'Apoyo',
    titleCourseTeam: 'Equipo de recorrido',
    openAcceptedBody: (email, eventName, role, name) => (
      <>
        Ahora descarga la app WindMaps e inicia sesión con el correo <strong>{email}</strong>. Verás el evento{' '}
        <strong>{eventName}</strong> en "Mis eventos" y podrás actuar como {role} <strong>{name}</strong>.
      </>
    ),
    teamSentBody: 'Tu equipo se envió a la organización para su aprobación. Te avisaremos cuando sea aprobado.',
    statusTitle: {
      approved: 'Invitación ya aprobada',
      rejected: 'Invitación rechazada',
      expired: 'Invitación vencida',
    },
    unavailable: 'Esta invitación ya no está disponible.',
    openBody: (eventName, name) => (
      <>
        {eventName}. Eres el responsable de <strong>{name}</strong>. Ingresa el correo que vas a usar para entrar en
        la app WindMaps. Así es como la app te va a reconocer.
      </>
    ),
    yourEmail: 'Tu correo',
    emailPlaceholder: 'juan@correo.com',
    emailHint:
      'Este es el correo que vas a usar para registrarte en la app. Puede ser Gmail, Outlook o cualquier otro, pero tiene que ser el mismo en los dos lugares.',
    teamTitle: (eventName) => `Invitación: ${eventName}`,
    teamBody: (name) =>
      `Hola, ${name}. Confirma los datos de tu equipo abajo. Después, la organización aprueba el registro manualmente.`,
    teamNameLabel: 'Nombre del equipo / fabricante',
    slugLabel: 'Slug',
    slugPlaceholder: 'faster-kite',
    slugHint: 'Identificador apto para URL. Se usa en el CSV de atletas.',
    teamColorLabel: 'Color del equipo',
    contactNameLabel: 'Nombre del responsable',
    acceptInvite: 'Aceptar invitación',
  },
});
